import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/** An option shown in a {@link FilterMultiSelectContent} list. */
export interface FilterMultiSelectItem {
  id: string;
  name: string;
}

interface FilterMultiSelectContentProps {
  items: FilterMultiSelectItem[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  emptyText: string;
  search?: {
    value: string;
    onChange: (value: string) => void;
    loading: boolean;
  };
}

/**
 * Dropdown body for an id multi-select filter: a searchable checkbox list with
 * a sticky Clear footer. Filters locally by default; passing `search` switches
 * to a caller-controlled (server-searched) input with a loading empty state.
 * @param items - The options to list, in display order
 * @param selected - Currently selected ids
 * @param onChange - Called with the next selected ids
 * @param placeholder - The search input placeholder
 * @param emptyText - Message shown when no options match
 * @param search - Controlled search state; presence disables local filtering
 * @returns The multi-select filter dropdown content
 */
export function FilterMultiSelectContent({
  items,
  selected,
  onChange,
  placeholder,
  emptyText,
  search,
}: FilterMultiSelectContentProps) {
  const toggle = (id: string) => {
    onChange(
      selected.includes(id)
        ? selected.filter((selectedId) => selectedId !== id)
        : [...selected, id],
    );
  };

  return (
    <Command shouldFilter={!search}>
      <CommandInput
        placeholder={placeholder}
        value={search?.value}
        onValueChange={search?.onChange}
      />
      <CommandList>
        <CommandEmpty>
          {search?.loading ? (
            <Loader2 className="mx-auto size-4 animate-spin" />
          ) : (
            emptyText
          )}
        </CommandEmpty>
        <CommandGroup>
          {items.map((item) => (
            <CommandItem
              key={item.id}
              value={search ? item.id : item.name}
              onSelect={() => toggle(item.id)}
              className="gap-2"
            >
              <span className="flex-1">{item.name}</span>
              <Checkbox
                checked={selected.includes(item.id)}
                className="pointer-events-none"
              />
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      {selected.length > 0 && (
        <div className="border-t p-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-full text-muted-foreground"
            onClick={() => onChange([])}
          >
            Clear
          </Button>
        </div>
      )}
    </Command>
  );
}
